import React, { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveToolbarButton from '../../../../shared/ui/workflow/ResponsiveToolbarButton';
import ToolbarButtonGroup from '../../../../shared/ui/workflow/ToolbarButtonGroup';
import { WorkspaceStatusPill } from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import type {
  LearnedAutoFillMergeItem,
  LearnedAutoFillPatternV1,
} from '../../../../shared/lib/quickFill/learnedAutoFillLibrary';
import {
  buildQuickFillKeyLibraryTableModel,
  type QuickFillKeyLibraryDialogSource,
  type QuickFillKeyLibraryFilter,
  type QuickFillKeyLibrarySort,
  type QuickFillKeyLibrarySortDirection,
} from '../../lib/quickFillKeyLibraryTableModel';
import type { QuickFillKeyLibraryEditableField } from '../../lib/quickFillKeyLibraryDrafts';
import QuickFillKeyLibraryEntryRow from './QuickFillKeyLibraryEntryRow';
import {
  getKeyLibraryColumnHeaderSx,
  getKeyLibraryBlankSlateSx,
  getKeyLibraryCompactFieldSx,
  getKeyLibraryEmptyStateSx,
  getKeyLibraryPaginationSx,
  getKeyLibraryTableContainerSx,
  getKeyLibraryTableSurfaceSx,
  getKeyLibraryTableSx,
  getKeyLibraryToolbarSx,
} from './quickFillKeyLibraryStyles';

const HeaderCell: React.FC<{ label: string }> = ({ label }) => (
  <TableCell>
    <Box sx={getKeyLibraryColumnHeaderSx}>
      <Typography component="span" data-header-label>{label}</Typography>
    </Box>
  </TableCell>
);

export interface QuickFillKeyLibraryEntryPreviewProps {
  source: QuickFillKeyLibraryDialogSource;
  libraryEntries: readonly LearnedAutoFillPatternV1[];
  mergePlan: readonly LearnedAutoFillMergeItem[];
  builtInShadowedIds: ReadonlySet<string>;
  confirmedConflictIds: ReadonlySet<string>;
  selectedLocalEntryIndexes?: ReadonlySet<number>;
  onDraftEntryChange: (
    index: number, field: QuickFillKeyLibraryEditableField, value: string
  ) => void;
  onLibraryEntryChange: (
    index: number, field: QuickFillKeyLibraryEditableField, value: string
  ) => void;
  onDeleteLibraryEntry: (index: number) => void;
  onConfirmConflict: (id: string, confirmed: boolean) => void;
  onToggleLocalEntrySelection?: (index: number, selected: boolean) => void;
  onToggleVisibleLocalEntries?: (indexes: readonly number[], selected: boolean) => void;
  onRequestBulkDelete?: () => void;
  onAddLibraryEntry?: () => void;
}

const sortLabels: Record<QuickFillKeyLibrarySort, string> = {
  library: 'Dictionary order',
  family: 'Family',
  requiredKeys: 'Required keys',
  objectType: 'OBJECT-TYPE',
  skey: 'SKEY',
  updatedAt: 'Updated time',
};

const entryColumns = [
  { id: 'family', label: 'Family', width: '13.5rem' },
  { id: 'requiredKeys', label: 'Required keys', width: '16rem' },
  { id: 'objectType', label: 'OBJECT-TYPE', width: '10rem' },
  { id: 'skey', label: 'SKEY', width: '7.5rem' },
  { id: 'endValues', label: 'End values', width: '25rem' },
  { id: 'actions', label: 'Actions', width: '9rem' },
] as const;

const QuickFillKeyLibraryEntryPreview: React.FC<QuickFillKeyLibraryEntryPreviewProps> = ({
  source,
  libraryEntries,
  mergePlan,
  builtInShadowedIds,
  confirmedConflictIds,
  selectedLocalEntryIndexes = new Set(),
  onDraftEntryChange,
  onLibraryEntryChange,
  onDeleteLibraryEntry,
  onConfirmConflict,
  onToggleLocalEntrySelection,
  onToggleVisibleLocalEntries,
  onRequestBulkDelete,
  onAddLibraryEntry,
}) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState<QuickFillKeyLibrarySort>('library');
  const [sortDirection, setSortDirection] = useState<QuickFillKeyLibrarySortDirection>('ascending');
  const [filter, setFilter] = useState<QuickFillKeyLibraryFilter>('all');
  const [frozenSortIndexes, setFrozenSortIndexes] = useState<number[]>();
  const isManageMode = source === 'manage';

  useEffect(() => {
    setQuery('');
    setPage(0);
    setSortBy('library');
    setSortDirection('ascending');
    setFilter('all');
    setFrozenSortIndexes(undefined);
  }, [source]);

  const freezeCurrentSort = (
    nextSort: QuickFillKeyLibrarySort,
    nextDirection: QuickFillKeyLibrarySortDirection
  ) => {
    if (nextSort === 'library') {
      setFrozenSortIndexes(undefined);
      return;
    }
    const sorted = buildQuickFillKeyLibraryTableModel({
      source,
      libraryEntries,
      mergePlan,
      query: '',
      page: 0,
      rowsPerPage: Math.max(1, libraryEntries.length),
      sortBy: nextSort,
      sortDirection: nextDirection,
      filter: 'all',
      builtInShadowedIds,
    });
    setFrozenSortIndexes(sorted.visibleEntries.map(({ index }) => index));
  };

  const tableModel = useMemo(() => buildQuickFillKeyLibraryTableModel({
    source,
    libraryEntries,
    mergePlan,
    query,
    page,
    rowsPerPage,
    sortBy,
    sortDirection,
    filter,
    builtInShadowedIds,
    frozenSortIndexes,
  }), [
    builtInShadowedIds, filter, frozenSortIndexes, libraryEntries, mergePlan, page, query,
    rowsPerPage, sortBy, sortDirection, source,
  ]);

  const visibleIndexes = tableModel.visibleEntries.map(({ index }) => index);
  const selectedVisibleCount = visibleIndexes.filter((index) =>
    selectedLocalEntryIndexes.has(index)
  ).length;
  const allVisibleSelected = visibleIndexes.length > 0 &&
    selectedVisibleCount === visibleIndexes.length;
  const emptyMessage = source === 'manage'
    ? 'The local dictionary is empty. Add a local key or select Quick Fill rows to learn one.'
    : source === 'import'
      ? 'The imported dictionary contains no entries to merge.'
      : 'No complete selected rows are ready to add.';

  if (tableModel.indexedEntryCount === 0) {
    if (isManageMode) {
      return (
        <Paper
          component="section"
          aria-labelledby="key-library-empty-title"
          elevation={0}
          sx={getKeyLibraryBlankSlateSx}
        >
          <Stack spacing={1.25} alignItems="center">
            <Box data-key-library-empty-icon>
              <AutoAwesomeOutlinedIcon fontSize="small" />
            </Box>
            <Stack spacing={0.5} alignItems="center">
              <Typography id="key-library-empty-title" component="h3" variant="h6">
                No local keys yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PipeEX rules already cover common components. Add a local key for a family name
                that Auto-fill does not recognize.
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<AddIcon fontSize="small" />}
              onClick={onAddLibraryEntry}
            >
              Add your first local key
            </Button>
          </Stack>
        </Paper>
      );
    }

    return (
      <Paper role="status" variant="outlined" sx={getKeyLibraryEmptyStateSx}>
        <Typography variant="body2">{emptyMessage}</Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={0} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        sx={getKeyLibraryToolbarSx}
      >
        <TextField
          label={isManageMode ? 'Search dictionary' : 'Search pending additions'}
          size="small"
          value={query}
          onChange={(event) => { setQuery(event.target.value); setPage(0); }}
          sx={(theme) => getKeyLibraryCompactFieldSx(theme, 240)}
        />
        {isManageMode && (
          <>
            <FormControl
              size="small"
              sx={(theme) => getKeyLibraryCompactFieldSx(theme, 155)}
            >
              <InputLabel id="key-library-filter-label">Show</InputLabel>
              <Select
                labelId="key-library-filter-label"
                label="Show"
                value={filter}
                onChange={(event) => { setFilter(event.target.value as QuickFillKeyLibraryFilter); setPage(0); }}
              >
                <MenuItem value="all">All local keys</MenuItem>
                <MenuItem value="usable">Usable learned keys</MenuItem>
                <MenuItem value="shadowed">Shadowed by built-ins</MenuItem>
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={(theme) => getKeyLibraryCompactFieldSx(theme, 155)}
            >
              <InputLabel id="key-library-sort-label">Sort by</InputLabel>
              <Select
                labelId="key-library-sort-label"
                label="Sort by"
                value={sortBy}
                onChange={(event) => {
                  const next = event.target.value as QuickFillKeyLibrarySort;
                  setSortBy(next); freezeCurrentSort(next, sortDirection); setPage(0);
                }}
              >
                {Object.entries(sortLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToolbarButtonGroup aria-label="Local key table actions" density="compact">
              <ResponsiveToolbarButton
                size="small"
                icon={sortDirection === 'ascending'
                  ? <ArrowUpwardOutlinedIcon fontSize="small" />
                  : <ArrowDownwardOutlinedIcon fontSize="small" />}
                label={sortDirection === 'ascending' ? 'Ascending' : 'Descending'}
                onClick={() => {
                  const next = sortDirection === 'ascending' ? 'descending' : 'ascending';
                  setSortDirection(next); freezeCurrentSort(sortBy, next); setPage(0);
                }}
              />
              <ResponsiveToolbarButton
                size="small"
                color="inherit"
                icon={<DeleteOutlineIcon fontSize="small" />}
                label={`Delete selected (${selectedLocalEntryIndexes.size})`}
                disabled={selectedLocalEntryIndexes.size === 0}
                onClick={onRequestBulkDelete}
                sx={(theme) => ({ color: theme.palette.error.main })}
              />
            </ToolbarButtonGroup>
          </>
        )}
        <Box sx={{ ml: { md: 'auto' } }}>
          <WorkspaceStatusPill
            label={`${tableModel.filteredEntryCount} ${tableModel.filteredEntryCount === 1 ? 'entry' : 'entries'} shown`}
            tone={tableModel.filteredEntryCount ? 'success' : 'neutral'}
            size="compact"
          />
        </Box>
      </Stack>
      {tableModel.filteredEntryCount === 0 ? (
        <Paper role="status" variant="outlined" sx={getKeyLibraryEmptyStateSx}>
          <Typography variant="body2">No dictionary entries match these filters.</Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={getKeyLibraryTableSurfaceSx}>
          <TableContainer sx={getKeyLibraryTableContainerSx}>
            <Table
              stickyHeader
              size="small"
              aria-label={isManageMode ? 'Existing dictionary entries' : 'Pending dictionary entries'}
              data-workspace-grid="quick-fill"
              sx={getKeyLibraryTableSx}
            >
              <colgroup>
                {isManageMode && <col style={{ width: '3rem' }} />}
                {entryColumns.map((column) => (
                  <col key={column.id} style={{ width: column.width }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  {isManageMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allVisibleSelected}
                        indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                        onChange={(event) => onToggleVisibleLocalEntries?.(
                          visibleIndexes, event.target.checked
                        )}
                        inputProps={{ 'aria-label': 'Select visible local keys' }}
                      />
                    </TableCell>
                  )}
                  {entryColumns.map((column) => (
                    <HeaderCell
                      key={column.id}
                      label={column.id === 'actions' && !isManageMode ? 'Status' : column.label}
                    />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableModel.visibleEntries.map(({ entry, index, mergeItem }) => (
                  <QuickFillKeyLibraryEntryRow
                    key={`${isManageMode ? 'library' : 'draft'}-${index}`}
                    entry={entry}
                    mergeItem={mergeItem}
                    isManageMode={isManageMode}
                    isShadowed={builtInShadowedIds.has(entry.id)}
                    isConflictConfirmed={confirmedConflictIds.has(entry.id)}
                    isSelected={selectedLocalEntryIndexes.has(index)}
                    onSelectedChange={(selected) => onToggleLocalEntrySelection?.(index, selected)}
                    onChange={(field, value) => isManageMode
                      ? onLibraryEntryChange(index, field, value)
                      : onDraftEntryChange(index, field, value)}
                    onDelete={() => onDeleteLibraryEntry(index)}
                    onConfirmConflict={(confirmed) => onConfirmConflict(entry.id, confirmed)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={tableModel.filteredEntryCount}
            page={tableModel.visiblePage}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
            sx={getKeyLibraryPaginationSx}
          />
        </Paper>
      )}
    </Stack>
  );
};

export default QuickFillKeyLibraryEntryPreview;
